<template>
    <div class="incident-overview-container gap-sm flex flex-col">
        <!-- showDisplayToggle now delegates to DataTable's display controls
             (ITDEVELOP-3170: view toggle + column picker) instead of the old
             hand-rolled list/grid buttons; the custom-grid-mode slot below is
             the cards renderer. Without the toggle the display stays fixed:
             cards below the breakpoint, table above (the dashboard embed). -->
        <DataTable
            :data="finalIncidents"
            :columns="incidentColumns"
            :loading="isLoading"
            :enable-pagination="false"
            :grid-mode="showDisplayToggle ? undefined : isCardView"
            :mobile-mode="showDisplayToggle ? 'cards' : 'auto'"
            :empty-state-config="{
                title: 'No incidents found',
                message: 'There are no incidents matching your filters.',
                icon: 'i-check-circle',
            }"
            @row-click="goToDetails"
        >
            <!-- Filters, search and the total ride DataTable's toolbar row so
                 they share one horizontal plane with the view toggle and the
                 Columns picker instead of stacking a row above the table
                 (ITDEVELOP-3170, the same slot /support uses). -->
            <template v-if="$slots.filters || showSearch || showTotal" #table-header-filters>
                <!-- flex-1 so the slot spans the toolbar row: filters keep the
                     left edge and the search/total group keeps the right, as
                     they did on their own row, with the display controls
                     landing after them. -->
                <div class="gap-md flex flex-1 flex-wrap items-center">
                    <slot name="filters" />

                    <div class="gap-md ml-auto flex w-fit items-center">
                        <div v-if="showSearch" class="input-wrapper w-64 pr-2.5">
                            <input
                                v-model="searchQuery"
                                type="search"
                                placeholder="Search fetched incidents..."
                                autocomplete="off"
                            />
                            <i class="i-search" />
                        </div>

                        <span v-if="showTotal" class="text-md text-primary max-w-20 min-w-20"
                            >Total: {{ totalIncidents }}</span
                        >
                    </div>
                </div>
            </template>

            <template #custom-grid-mode="{ data }">
                <div class="flex flex-col">
                    <!-- Column Headers -->
                    <div class="py-sm px-xs flex items-center justify-between lg:px-0">
                        <div class="gap-2xs flex flex-col">
                            <span class="text-primary font-bold uppercase">Reference</span>
                            <span class="text-(--text-muted)">Summary</span>
                        </div>
                        <span class="mr-sm text-primary font-bold uppercase">Status</span>
                    </div>

                    <div
                        v-for="incident in data"
                        :key="incident.incidentId"
                        class="py-md px-xs hover:bg-soft flex cursor-pointer items-center justify-between border-b border-(--border-subtle) transition-colors lg:px-0"
                        @click="goToDetails(incident)"
                    >
                        <div class="gap-md flex items-center">
                            <IncidentTypeIcon
                                class="h-6 w-6 text-(--icon-primary)"
                                :icon-key="typeToIconKey(incident.type)"
                                :type-title="incident.type"
                            />

                            <div class="gap-xs flex flex-col">
                                <router-link
                                    :to="{
                                        name: 'incident-details',
                                        params: { incidentId: incident.incidentId },
                                    }"
                                    class="text-link"
                                    @click.stop
                                >
                                    {{ incident.externalReference || incident.incidentId }}
                                </router-link>
                                <span class="text-md text-primary">{{ incident.summary }}</span>
                            </div>
                        </div>

                        <div class="gap-sm flex items-center">
                            <TagComponent
                                v-if="incident.priority"
                                :value="
                                    incident.priority.charAt(0).toUpperCase() + incident.priority.slice(1).toLowerCase()
                                "
                                :variant="
                                    incident.priority.toLowerCase() === 'critical'
                                        ? 'danger'
                                        : incident.priority.toLowerCase() === 'high'
                                          ? 'warning'
                                          : incident.priority.toLowerCase() === 'medium'
                                            ? 'gold'
                                            : 'success'
                                "
                                class="text-xs font-bold uppercase"
                            />
                            <TagComponent
                                :value="incident.status"
                                :show-dot="true"
                                class="text-xs font-bold uppercase"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </DataTable>

        <!-- Loading indicator for infinite scroll -->
        <div v-if="isLoadingMore" class="flex justify-center py-4">
            <p class="text-(--text-muted)">Loading more...</p>
        </div>

        <!-- Intersection Observer Sentinel -->
        <div
            v-if="hasMoreData && !isLoadingMore && finalIncidents.length > 0 && !disablePagination"
            ref="sentinelRef"
            class="h-1"
        ></div>

        <!-- End of results indicator -->
        <div v-if="!hasMoreData && finalIncidents.length > 0 && !disablePagination" class="flex justify-center py-4">
            <p class="text-(--text-muted)">No more results</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, h, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useIsDesktop } from '@/composables/isDesktop';
import DataTable from '@/components/tables/DataTable.vue';
import { createTextColumn, createStatusColumn, createPriorityColumn } from '@/components/tables/columns/factories';
import type { ColumnDef, CellContext } from '@tanstack/vue-table';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import {
    useIncidentStore,
    typeToIconKey,
    type IncidentFilterData,
    type IncidentDto,
    type IncidentLocation,
} from '@/stores/incident';
import IncidentTypeIcon from '@/components/incident/IncidentTypeIcon.vue';
import { formatDate, formatDateTime } from '@/stores/helpers';
import { humanizeIdentifier } from '@/lib/string-format';
import { formatIncidentPriority } from '@/lib/incident-detail-view-model';

type Incident = IncidentDto;

const props = withDefaults(
    defineProps<{
        filters: IncidentFilterData;
        itemCount?: number;
        displayMode?: 'list' | 'grid';
        showSearch?: boolean;
        showDisplayToggle?: boolean;
        showTotal?: boolean;
        enablePolling?: boolean;
        disablePagination?: boolean;
    }>(),
    {
        showSearch: false,
        showDisplayToggle: false,
        showTotal: false,
        enablePolling: false,
        disablePagination: false,
    },
);

const isLoading = ref(true);
const isDesktop = useIsDesktop();
// Fixed-display mode only (showDisplayToggle=false): an explicit displayMode
// wins, otherwise cards below the breakpoint. With the toggle on, DataTable's
// display controls own this choice instead.
const isCardView = computed(() => (props.displayMode ? props.displayMode === 'grid' : !isDesktop.value));
const searchQuery = ref('');
const router = useRouter();
const incidentStore = useIncidentStore();

// Pagination and Endless Scroll
const baseItemCount = 20;
const nextPageToken = ref<string | null>(null);
const hasMoreData = ref(true);
const isLoadingMore = ref(false);
const sentinelRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;
const pollingInterval = ref<number | null>(null);

const incidents = ref<Incident[]>([]);
const apiTotalCount = ref(0);

function goToDetails(incident: Incident) {
    router.push({ name: 'incident-details', params: { incidentId: incident.incidentId } });
}

async function fetchIncidents() {
    isLoading.value = true;
    try {
        const response = await incidentStore.getIncidents(props.filters, null, props.itemCount ?? baseItemCount);

        if (response) {
            incidents.value = response.data;
            nextPageToken.value = response.nextPageToken ?? null;
            hasMoreData.value = props.disablePagination ? false : !!response.nextPageToken;
            apiTotalCount.value = response.totalCount ?? 0;
        }
    } catch (e) {
        console.error('Failed to fetch incidents', e);
    } finally {
        isLoading.value = false;
    }
}

async function loadMoreIncidents() {
    if (!hasMoreData.value || isLoadingMore.value || !nextPageToken.value) return;

    isLoadingMore.value = true;
    try {
        const response = await incidentStore.getIncidents(
            props.filters,
            nextPageToken.value,
            props.itemCount ?? baseItemCount,
        );

        if (response) {
            incidents.value = [...incidents.value, ...response.data];
            nextPageToken.value = response.nextPageToken ?? null;
            hasMoreData.value = !!response.nextPageToken;
            apiTotalCount.value = response.totalCount ?? 0;
        }
    } catch (e) {
        console.error('Failed to load more incidents', e);
    } finally {
        isLoadingMore.value = false;
    }
}

function setupIntersectionObserver() {
    if (observer) observer.disconnect();

    observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && hasMoreData.value && !isLoadingMore.value) {
                loadMoreIncidents();
            }
        },
        { rootMargin: '200px' },
    );

    if (sentinelRef.value) observer.observe(sentinelRef.value);
}

function startPolling() {
    if (props.enablePolling && !pollingInterval.value) {
        pollingInterval.value = window.setInterval(() => {
            fetchIncidents();
        }, 60000);
    }
}

function stopPolling() {
    if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
    }
}

onMounted(() => {
    startPolling();
});

watch(sentinelRef, async (newVal) => {
    if (newVal) {
        await nextTick();
        setupIntersectionObserver();
    }
});

// Re-fetch when filters change (immediate handles the initial fetch on mount)
// Using JSON.stringify to avoid re-fetching when values are identical but object reference changes
watch(
    () => JSON.stringify(props.filters),
    () => {
        fetchIncidents();
    },
    { immediate: true },
);

onUnmounted(() => {
    if (observer) observer.disconnect();
    stopPolling();
});

const totalIncidents = computed(() => (isLoading.value ? 'N/A' : apiTotalCount.value));

const finalIncidents = computed(() => {
    let filtered = incidents.value;

    if (searchQuery.value) {
        const s = searchQuery.value.toLowerCase();
        filtered = filtered.filter((i) => {
            const haystack = [
                i.incidentId,
                i.externalReference,
                i.summary,
                i.type,
                humanizeIdentifier(i.type, { casing: 'title' }),
                i.status,
                i.priority,
                formatIncidentPriority(i.priority),
                i.location?.sites?.join(', '),
                i.reporterName,
                i.createdAt ? formatDate(i.createdAt) : undefined,
            ];
            return haystack.some((value) => value?.toLowerCase().includes(s));
        });
    }

    if (props.itemCount) {
        return filtered.slice(0, props.itemCount);
    }
    return filtered;
});

const incidentColumns: ColumnDef<Incident, unknown>[] = [
    {
        accessorKey: 'type',
        header: 'Type',
        cell: (info: CellContext<Incident, string>) => {
            const type = info.getValue();
            return h(
                'div',
                { class: 'flex w-full items-center' },
                h(IncidentTypeIcon, {
                    class: 'block h-6 w-6 text-(--icon-primary)',
                    iconKey: typeToIconKey(type),
                    typeTitle: type,
                }),
            );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            return rowA.original.type.localeCompare(rowB.original.type);
        },
    },
    {
        accessorKey: 'incidentId',
        header: 'Incident ID',
        cell: (info: CellContext<Incident, string>) => {
            const reference = info.getValue();
            return h(
                RouterLink,
                {
                    to: {
                        name: 'incident-details',
                        params: { incidentId: info.row.original.incidentId },
                    },
                    class: 'text-link',
                },
                {
                    default: () => reference || info.row.original.incidentId,
                },
            );
        },
    },
    createTextColumn<Incident>('summary', 'Summary'),
    createStatusColumn<Incident>('status', 'Status'),
    createPriorityColumn<Incident>('priority', 'Priority'),
    {
        accessorKey: 'location',
        header: 'Location',
        cell: (info: CellContext<Incident, IncidentLocation | undefined>) => {
            const location = info.getValue();
            if (!location?.sites?.length) return '';
            return location.sites.join(', ');
        },
    },
    createTextColumn<Incident>('reporterName', 'Reported By'),
    {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (info: CellContext<Incident, string>) => {
            const date = info.getValue();
            if (!date) return '';
            return formatDateTime(date);
        },
        enableSorting: true,
    },
];
</script>

<style>
/* Common base for icons */
.incident-overview-container [class^='i-'] {
    height: 1rem;
    width: 1rem;
    background-size: cover;
    display: inline-block;
}

.incident-overview-container .i-fire {
    background-image: url('@/assets/icons/warning.svg');
}

.incident-overview-container .i-database {
    background-image: url('@/assets/icons/rack.svg');
}

.incident-overview-container .i-binoculars {
    background-image: url('@/assets/icons/search.svg');
}

.incident-overview-container .i-system {
    background-image: url('@/assets/icons/cog.svg');
}

.incident-overview-container .i-box {
    background-image: url('@/assets/icons/pod.svg');
}

.incident-overview-container .i-screen {
    background-image: url('@/assets/icons/card.svg');
}

.incident-overview-container .i-stack {
    background-image: url('@/assets/icons/nodes.svg');
}

.incident-overview-container .i-server {
    background-image: url('@/assets/icons/rack.svg');
}

.incident-overview-container .i-warning {
    background-image: url('@/assets/icons/warning.svg');
}
</style>
